import {
  FeatureFlag,
  AppEnvironment,
  getEnvironmentDisplayName,
} from "src/lib/featureFlagsService";
import { FaApple } from "react-icons/fa";
import { FcAndroidOs } from "react-icons/fc";
import { FaGlobe } from "react-icons/fa";

interface CreateFlagModalProps {
  show: boolean;
  onClose: () => void;
  onSubmit: (flag: FeatureFlag) => void;
  newFlagKey: string;
  setNewFlagKey: (key: string) => void;
  newFlagData: FeatureFlag;
  setNewFlagData: (data: FeatureFlag) => void;
}
const CreateFlagModal: React.FC<CreateFlagModalProps> = ({
  show,
  onClose,
  onSubmit,
  newFlagKey,
  setNewFlagKey,
  newFlagData,
  setNewFlagData,
}) => {
  const getPlatformIcon = (platform: string) => {
    switch (platform.toLowerCase()) {
      case "ios":
        return <FaApple className="text-gray-300" />;
      case "android":
        return <FcAndroidOs />;
      case "web":
        return <FaGlobe className="text-blue-400" />;
      default:
        return null;
    }
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-gray-900 border border-white/20 rounded-2xl p-6 w-full max-w-md mx-4">
        <h2 className="text-2xl font-bold mb-4">Create New Feature Flag</h2>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Flag Key *
            </label>
            <input
              type="text"
              value={newFlagKey}
              onChange={(e) => setNewFlagKey(e.target.value.toLowerCase())}
              placeholder="e.g., new_feature"
              className="w-full px-4 py-2 rounded-lg border bg-gray-800 border-white/20 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              Lowercase letters, numbers, and underscores only
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Description
            </label>
            <input
              type="text"
              value={newFlagData.description || ""}
              onChange={(e) =>
                setNewFlagData({
                  ...newFlagData,
                  description: e.target.value,
                })
              }
              placeholder="What does this flag control?"
              className="w-full px-4 py-2 rounded-lg border bg-gray-800 border-white/20 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Min Build Number
            </label>
            <input
              type="number"
              value={newFlagData.min_build || ""}
              onChange={(e) =>
                setNewFlagData({
                  ...newFlagData,
                  min_build: e.target.value
                    ? parseInt(e.target.value)
                    : undefined,
                })
              }
              placeholder="Optional"
              className="w-full px-4 py-2 rounded-lg border bg-gray-800 border-white/20 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Platforms
            </label>
            <div className="space-y-2">
              {["ios", "android"].map((platform) => (
                <label key={platform} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={newFlagData.platforms?.includes(platform) || false}
                    onChange={(e) => {
                      const platforms = newFlagData.platforms || [];
                      if (e.target.checked) {
                        setNewFlagData({
                          ...newFlagData,
                          platforms: [...platforms, platform],
                        });
                      } else {
                        setNewFlagData({
                          ...newFlagData,
                          platforms: platforms.filter((p) => p !== platform),
                        });
                      }
                    }}
                    className="rounded"
                  />
                  <span className="capitalize">{platform}</span>
                  <span className="text-md font-medium text-gray-300">
                    {getPlatformIcon(platform)}
                  </span>
                </label>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Leave unchecked for all platforms
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Environments
            </label>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {Object.values(AppEnvironment).map((env) => (
                <label key={env} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={newFlagData.environment?.includes(env) || false}
                    onChange={(e) => {
                      const environments = newFlagData.environment || [];
                      if (e.target.checked) {
                        setNewFlagData({
                          ...newFlagData,
                          environment: [...environments, env],
                        });
                      } else {
                        setNewFlagData({
                          ...newFlagData,
                          environment: environments.filter((e) => e !== env),
                        });
                      }
                    }}
                    className="rounded"
                  />
                  <span className="text-sm">
                    {getEnvironmentDisplayName(env)}
                  </span>
                </label>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Leave unchecked to disable for all environments
            </p>
          </div>

          <div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={newFlagData.enabled}
                onChange={(e) =>
                  setNewFlagData({
                    ...newFlagData,
                    enabled: e.target.checked,
                  })
                }
                className="rounded"
              />
              <span className="text-sm font-medium text-gray-300">
                Enable immediately
              </span>
            </label>
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button
            onClick={() => onSubmit(newFlagData)}
            className="flex-1 px-4 py-2 rounded-lg bg-indigo-500 hover:bg-indigo-600 text-white font-medium transition-colors disabled:opacity-50"
          >
            Create Flag
          </button>
          <button
            onClick={() => {
              onClose();
              setNewFlagKey("");
              setNewFlagData({
                enabled: false,
                description: "",
                environment: [],
              });
            }}
            className="flex-1 px-4 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-white font-medium transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateFlagModal;
